import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { FC } from 'react';
import type { FollowUpConfigDraft } from 'services/types/followUpConfig';

import ClampedNumberField from './ClampedNumberField';

interface ConfigurationSectionProps {
  followUpConfigDraft: FollowUpConfigDraft;
  isEditing: boolean;
  setFollowUpConfigDraft?: (followUpConfigDraft: FollowUpConfigDraft) => void;
}

const ConfigurationSection: FC<ConfigurationSectionProps> = ({
  followUpConfigDraft,
  isEditing,
  setFollowUpConfigDraft,
}) => {
  return (
    <Card sx={{ mb: 1.5, boxShadow: 0, borderRadius: 0 }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 0.5, fontWeight: 600 }}>
          Configuración
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            mb: 1.5,
            display: 'block',
          }}
        >
          Se ejecuta por lotes cada 20 minutos
        </Typography>
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            {isEditing ? (
              <ClampedNumberField
                label="Máximos mensajes consecutivos de este tipo"
                value={followUpConfigDraft.maxConsecutive}
                onChange={(next) => setFollowUpConfigDraft?.({ ...followUpConfigDraft, maxConsecutive: next })}
                min={1}
                fullWidth
              />
            ) : (
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                    display: 'block',
                    mb: 0.5,
                  }}
                >
                  Máximos mensajes consecutivos de este tipo
                </Typography>
                <Typography variant="body2">{followUpConfigDraft.maxConsecutive}</Typography>
              </Box>
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            {isEditing ? (
              <ClampedNumberField
                label="Máximo de mensajes de este tipo en la conversación"
                value={followUpConfigDraft.maxAmountInConversation}
                onChange={(next) => setFollowUpConfigDraft?.({ ...followUpConfigDraft, maxAmountInConversation: next })}
                min={1}
                fullWidth
              />
            ) : (
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                    display: 'block',
                    mb: 0.5,
                  }}
                >
                  Máximo de mensajes de este tipo en la conversación
                </Typography>
                <Typography variant="body2">{followUpConfigDraft.maxAmountInConversation}</Typography>
              </Box>
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            {isEditing ? (
              <ClampedNumberField
                label="Límite de clientes por ejecución"
                value={followUpConfigDraft.clientsBatchSize || 0}
                onChange={(next) => setFollowUpConfigDraft?.({ ...followUpConfigDraft, clientsBatchSize: next })}
                min={1}
                fullWidth
              />
            ) : (
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                    display: 'block',
                    mb: 0.5,
                  }}
                >
                  Límite de clientes por ejecución
                </Typography>
                <Typography variant="body2">{followUpConfigDraft.clientsBatchSize || 0}</Typography>
              </Box>
            )}
          </Grid>
          {isEditing && (
            <Grid size={12}>
              <Accordion sx={{ boxShadow: 'none', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    Configuración Avanzada
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Grid container spacing={1.5}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Identificador de mensaje anterior (avanzado)"
                        value={followUpConfigDraft.previousMessageKeepAliveIdentifier || ''}
                        onChange={(e) =>
                          setFollowUpConfigDraft?.({
                            ...followUpConfigDraft,
                            previousMessageKeepAliveIdentifier: e.target.value === '' ? null : e.target.value,
                          })
                        }
                        fullWidth
                        helperText="Identificador del mensaje anterior que debe mantenerse activo"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="IDs de seguimientos previos requeridos (avanzado)"
                        value={followUpConfigDraft.requiredPreviousFollowUpsIds?.join(',') || ''}
                        onChange={(e) =>
                          setFollowUpConfigDraft?.({
                            ...followUpConfigDraft,
                            requiredPreviousFollowUpsIds:
                              e.target.value === '' ? null : e.target.value.split(',').map(Number),
                          })
                        }
                        fullWidth
                        helperText="IDs de follow-ups que deben haberse ejecutado previamente (separados por coma)"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Nombre de la función a ejecutar (avanzado)"
                        value={followUpConfigDraft.functionName || ''}
                        onChange={(e) =>
                          setFollowUpConfigDraft?.({
                            ...followUpConfigDraft,
                            functionName: e.target.value === '' ? null : e.target.value,
                          })
                        }
                        fullWidth
                        helperText="Nombre de la función personalizada a ejecutar"
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
          )}
          {!isEditing &&
            (followUpConfigDraft.previousMessageKeepAliveIdentifier !== null ||
              followUpConfigDraft.requiredPreviousFollowUpsIds !== null ||
              followUpConfigDraft.functionName !== null) && (
              <>
                <Grid size={12}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: 'text.secondary',
                      mt: 1,
                      mb: 1,
                      fontWeight: 600,
                    }}
                  >
                    Configuración Avanzada
                  </Typography>
                </Grid>
                {followUpConfigDraft.previousMessageKeepAliveIdentifier !== null && (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontWeight: 500,
                          display: 'block',
                          mb: 0.5,
                        }}
                      >
                        Identificador de mensaje anterior (avanzado)
                      </Typography>
                      <Typography variant="body2">{followUpConfigDraft.previousMessageKeepAliveIdentifier}</Typography>
                    </Box>
                  </Grid>
                )}
                {followUpConfigDraft.requiredPreviousFollowUpsIds !== null && (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontWeight: 500,
                          display: 'block',
                          mb: 0.5,
                        }}
                      >
                        IDs de seguimientos previos requeridos (avanzado)
                      </Typography>
                      <Typography variant="body2">
                        {followUpConfigDraft.requiredPreviousFollowUpsIds.join(', ')}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {followUpConfigDraft.functionName !== null && (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontWeight: 500,
                          display: 'block',
                          mb: 0.5,
                        }}
                      >
                        Nombre de la función a ejecutar (avanzado)
                      </Typography>
                      <Typography variant="body2">{followUpConfigDraft.functionName}</Typography>
                    </Box>
                  </Grid>
                )}
              </>
            )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default ConfigurationSection;
